import { useState, useEffect, useRef, useCallback } from "react";
import franceMap from "@svg-maps/france.regions";

/* ─────────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Barlow+Condensed:wght@200;300;400;500;600;700&family=Barlow:wght@300;400;500&display=swap');

  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

  :root {
    --obsidian:    #07070d;
    --dark-1:      #0d0d16;
    --dark-2:      #13131f;
    --dark-3:      #1c1c2e;
    --dark-4:      #252538;
    --dark-5:      #2e2e46;
    --gold-bright: #f5e090;
    --gold:        #d4af37;
    --gold-mid:    #b8962e;
    --gold-dark:   #7a5f14;
    --gold-dim:    #3d300a;
    --blue-fr:     #002395;
    --white-fr:    #f4f4ef;
    --red-fr:      #ed2939;
    --text-dim:    rgba(212,175,55,0.45);
    --text-muted:  rgba(244,244,239,0.45);
    --text-body:   rgba(244,244,239,0.75);
  }

  html { scroll-behavior: smooth; }

  body {
    background: var(--obsidian);
    color: var(--white-fr);
    font-family: 'Barlow', sans-serif;
    font-weight: 300;
    overflow-x: hidden;
  }

  /* ── CUSTOM CURSOR — pointer:fine = real mouse only ── */
  @media (pointer: fine) {
    body, body * { cursor: none !important; }
  }

  .cursor-dot,
  .cursor-ring {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    pointer-events: none;
    z-index: 50000;
    will-change: transform, width, height, opacity;
    border-radius: 50%;
    transform: translate3d(-100px, -100px, 0) translate(-50%, -50%);
  }

  @media (pointer: fine) {
    .cursor-dot {
      display: block;
      width: 8px;
      height: 8px;
      background: var(--gold);
      z-index: 50001;
    }

    .cursor-ring {
      display: block;
      width: 34px;
      height: 34px;
      border: 1px solid rgba(212,175,55,0.7);
      transition:
        width 0.25s ease,
        height 0.25s ease,
        opacity 0.25s ease;
    }

    .cursor-ring.hovering {
      width: 52px;
      height: 52px;
      opacity: 0.35;
    }
  }

  /* ── NAV ── */
  nav {
    position: fixed;
    top: 0; left: 0; right: 0;
    z-index: 200;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 26px 60px;
    transition: background 0.5s ease, padding 0.4s ease;
  }
  nav.scrolled {
    background: rgba(7,7,13,0.94);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    padding: 16px 60px;
    border-bottom: 1px solid rgba(212,175,55,0.12);
  }
  .nav-logo {
    display: flex;
    align-items: center;
    gap: 13px;
    text-decoration: none;
    flex-shrink: 0;
  }
  .nav-logo-emblem { width: 36px; height: 36px; }
  .nav-logo-text {
    font-family: 'Cormorant Garamond', serif;
    font-size: 1.05rem;
    font-weight: 500;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: var(--gold);
    line-height: 1.1;
  }
  .nav-logo-sub {
    display: block;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 0.52rem;
    font-weight: 300;
    letter-spacing: 0.36em;
    color: var(--text-muted);
    margin-top: 2px;
  }
  .nav-links {
    display: flex;
    align-items: center;
    gap: 40px;
    list-style: none;
  }
  .nav-links a {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 0.7rem;
    font-weight: 500;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: var(--text-muted);
    text-decoration: none;
    transition: color 0.3s;
    position: relative;
    white-space: nowrap;
  }
  .nav-links a::after {
    content: '';
    position: absolute;
    bottom: -4px; left: 0;
    width: 0; height: 1px;
    background: var(--gold);
    transition: width 0.35s ease;
  }
  .nav-links a:hover { color: var(--gold); }
  .nav-links a:hover::after { width: 100%; }
  .nav-cta {
    color: var(--obsidian) !important;
    background: var(--gold);
    padding: 10px 22px;
    transition: background 0.3s !important;
  }
  .nav-cta::after { display: none !important; }
  .nav-cta:hover { background: var(--gold-bright) !important; }

  /* hamburger — hidden on desktop */
  .nav-hamburger {
    display: none;
    flex-direction: column;
    gap: 5px;
    background: none;
    border: none;
    padding: 6px;
    z-index: 300;
    position: relative;
  }
  .nav-hamburger span {
    display: block;
    width: 24px; height: 1.5px;
    background: var(--gold);
    transition: transform 0.3s ease, opacity 0.3s ease;
    transform-origin: center;
  }
  .nav-hamburger.open span:nth-child(1) { transform: translateY(6.5px) rotate(45deg); }
  .nav-hamburger.open span:nth-child(2) { opacity: 0; transform: scaleX(0); }
  .nav-hamburger.open span:nth-child(3) { transform: translateY(-6.5px) rotate(-45deg); }

  /* mobile drawer */
  .nav-drawer {
    display: none;
    position: fixed;
    inset: 0;
    background: rgba(7,7,13,0.97);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    z-index: 150;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.35s ease;
  }
  .nav-drawer.open { opacity: 1; pointer-events: all; }
  .nav-drawer-link {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(1.8rem, 6vw, 2.6rem);
    font-weight: 300;
    letter-spacing: 0.06em;
    color: var(--white-fr);
    text-decoration: none;
    padding: 18px 40px;
    text-align: center;
    transition: color 0.3s;
    border-bottom: 1px solid rgba(212,175,55,0.08);
    width: 100%;
  }
  .nav-drawer-link:last-of-type { border-bottom: none; }
  .nav-drawer-link:hover, .nav-drawer-link:active { color: var(--gold); }
  .nav-drawer-cta {
    margin-top: 28px;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    background: var(--gold);
    color: var(--obsidian);
    padding: 16px 44px;
    text-decoration: none;
  }
  .nav-drawer-tricolor {
    display: flex;
    gap: 4px;
    margin-top: 36px;
  }
  .nav-drawer-tricolor span { width: 26px; height: 2px; }

  /* ── SECTIONS ── */
  section { position: relative; overflow: hidden; }

  /* ── HERO ── */
  .hero {
    height: 100svh;
    min-height: 600px;
    display: flex;
    align-items: center;
    justify-content: center;
    background:
      radial-gradient(ellipse 80% 60% at 50% 100%, rgba(0,35,149,0.18) 0%, transparent 60%),
      radial-gradient(ellipse 60% 40% at 80% 20%, rgba(212,175,55,0.06) 0%, transparent 50%),
      linear-gradient(160deg, var(--obsidian) 0%, var(--dark-1) 40%, #0a0a18 70%, #06060e 100%);
  }
  .hero-noise {
    position: absolute; inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E");
    opacity: 0.4; pointer-events: none;
  }
  .hero-lines {
    position: absolute; inset: 0;
    background-image: repeating-linear-gradient(90deg, rgba(212,175,55,0.025) 0px, rgba(212,175,55,0.025) 1px, transparent 1px, transparent 80px);
    pointer-events: none;
  }
  .hero-content {
    position: relative; z-index: 2;
    text-align: center;
    padding: 0 28px;
    max-width: 960px;
    width: 100%;
  }
  .hero-tricolor {
    display: flex; gap: 5px;
    justify-content: center;
    margin-bottom: 36px;
    opacity: 0;
    animation: fadeUp 0.8s ease 0.2s forwards;
  }
  .tricolor-bar { height: 2px; width: 36px; }
  .hero-overline {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: clamp(0.52rem, 1.5vw, 0.65rem);
    font-weight: 500;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    color: var(--text-dim);
    margin-bottom: 22px;
    opacity: 0;
    animation: fadeUp 0.8s ease 0.4s forwards;
  }
  .hero-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(3rem, 9vw, 7.2rem);
    font-weight: 300;
    line-height: 0.92;
    letter-spacing: -0.01em;
    color: var(--white-fr);
    opacity: 0;
    animation: fadeUp 1s ease 0.6s forwards;
  }
  .hero-title em { font-style: italic; color: var(--gold); }
  .hero-title-brand {
    display: block;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: clamp(0.7rem, 2.5vw, 1.4rem);
    font-weight: 200;
    letter-spacing: 0.48em;
    text-transform: uppercase;
    color: var(--gold-mid);
    margin-top: 20px;
    opacity: 0;
    animation: fadeUp 0.8s ease 0.85s forwards;
  }
  .hero-divider {
    width: 1px; height: 40px;
    background: linear-gradient(to bottom, transparent, var(--gold-dark), transparent);
    margin: 30px auto;
    opacity: 0;
    animation: fadeUp 0.8s ease 1s forwards;
  }
  .hero-tagline {
    font-family: 'Barlow', sans-serif;
    font-size: clamp(0.75rem, 2vw, 0.88rem);
    font-weight: 300;
    letter-spacing: 0.1em;
    color: var(--text-body);
    line-height: 1.9;
    max-width: 480px;
    margin: 0 auto 44px;
    opacity: 0;
    animation: fadeUp 0.8s ease 1.1s forwards;
  }
  .hero-actions {
    display: flex;
    gap: 14px;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    opacity: 0;
    animation: fadeUp 0.8s ease 1.3s forwards;
  }
  .btn-primary {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: var(--obsidian);
    background: linear-gradient(135deg, var(--gold-bright) 0%, var(--gold) 50%, var(--gold-mid) 100%);
    padding: 15px 32px;
    text-decoration: none;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    box-shadow: 0 0 34px rgba(212,175,55,0.2);
    white-space: nowrap;
  }
  .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 40px rgba(212,175,55,0.38); }
  .btn-secondary {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 0.68rem;
    font-weight: 500;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: var(--gold);
    text-decoration: none;
    border: 1px solid rgba(212,175,55,0.3);
    padding: 15px 32px;
    transition: border-color 0.3s, background 0.3s;
    white-space: nowrap;
  }
  .btn-secondary:hover { border-color: var(--gold); background: rgba(212,175,55,0.06); }
  .hero-scroll {
    position: absolute;
    bottom: 28px; left: 50%;
    transform: translateX(-50%);
    display: flex; flex-direction: column;
    align-items: center; gap: 10px;
    opacity: 0;
    animation: fadeIn 1s ease 1.8s forwards;
  }
  .hero-scroll span {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 0.5rem;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: var(--text-dim);
  }
  .scroll-line {
    width: 1px; height: 42px;
    background: linear-gradient(to bottom, var(--gold-dim), transparent);
    animation: scrollPulse 2s ease-in-out infinite;
  }

  /* ── IDENTITY BAND ── */
  .identity-band {
    background: linear-gradient(90deg, var(--blue-fr) 0%, var(--dark-3) 33%, var(--dark-3) 66%, var(--red-fr) 100%);
    padding: 1px 0;
  }
  .identity-band-inner {
    background: var(--dark-2);
    margin: 1px 0;
    display: flex;
    align-items: stretch;
    flex-wrap: wrap;
  }
  .identity-stat {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 20px 24px;
    flex: 1;
    min-width: 130px;
    justify-content: center;
    border-right: 1px solid rgba(212,175,55,0.1);
  }
  .identity-stat:last-child { border-right: none; }
  .stat-number {
    font-family: 'Cormorant Garamond', serif;
    font-size: 1.7rem;
    font-weight: 400;
    color: var(--gold);
    line-height: 1;
    flex-shrink: 0;
  }
  .stat-label {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 0.58rem;
    font-weight: 400;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-muted);
    line-height: 1.5;
  }

  /* ── SECTION COMMONS ── */
  .section-header { text-align: center; margin-bottom: 68px; }
  .section-overline {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 0.62rem;
    font-weight: 500;
    letter-spacing: 0.55em;
    text-transform: uppercase;
    color: var(--gold-mid);
    margin-bottom: 18px;
    display: flex; align-items: center; justify-content: center; gap: 14px;
  }
  .section-overline::before, .section-overline::after {
    content: '';
    display: block; width: 26px; height: 1px;
    background: var(--gold-dark);
  }
  .section-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(2rem, 5vw, 3.8rem);
    font-weight: 300;
    line-height: 1.05;
    color: var(--white-fr);
  }
  .section-title em { font-style: italic; color: var(--gold); }
  .section-subtitle {
    font-family: 'Barlow', sans-serif;
    font-size: clamp(0.78rem, 1.8vw, 0.85rem);
    font-weight: 300;
    color: var(--text-body);
    letter-spacing: 0.04em;
    line-height: 1.9;
    max-width: 500px;
    margin: 20px auto 0;
  }

  /* ── ADN ── */
  .adn-section {
    padding: 110px 60px;
    background: linear-gradient(180deg, var(--dark-1) 0%, var(--dark-2) 50%, var(--dark-1) 100%);
  }
  .adn-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 80px;
    max-width: 1140px;
    margin: 0 auto;
    align-items: center;
  }
  .adn-visual { position: relative; }
  .adn-frame {
    aspect-ratio: 4/5;
    background: linear-gradient(135deg, var(--dark-3) 0%, var(--dark-4) 50%, var(--dark-3) 100%);
    border: 1px solid rgba(212,175,55,0.15);
    position: relative; overflow: hidden;
    display: flex; align-items: center; justify-content: center;
  }
  .adn-frame::before {
    content: '';
    position: absolute; inset: 12px;
    border: 1px solid rgba(212,175,55,0.06);
  }
  .adn-frame-corner {
    position: absolute;
    width: 18px; height: 18px;
    border-color: var(--gold-mid); border-style: solid;
  }
  .adn-frame-corner.tl { top: 6px; left: 6px;   border-width: 1px 0 0 1px; }
  .adn-frame-corner.tr { top: 6px; right: 6px;  border-width: 1px 1px 0 0; }
  .adn-frame-corner.bl { bottom: 6px; left: 6px;  border-width: 0 0 1px 1px; }
  .adn-frame-corner.br { bottom: 6px; right: 6px; border-width: 0 1px 1px 0; }
  .adn-made-france-box {
    position: absolute;
    bottom: -20px;
    right: -18px;

    width: 96px;
    height: 110px;

    background: #ffffff;
    border: 1px solid rgba(212, 175, 55, 0.45);
    box-shadow: 0 18px 42px rgba(0, 0, 0, 0.42);

    display: flex;
    align-items: center;
    justify-content: center;

    padding: 16px 12px;
    z-index: 5;
  }
  .made-france-logo {
    width: 68px;
    height: auto;
    display: block;
    object-fit: contain;
  }
  @media (max-width: 768px) {
    .adn-made-france-box {
      width: 78px;
      height: 116px;
      bottom: -14px;
      right: -12px;
      padding: 12px 9px;
    }

    .made-france-logo {
      width: 54px;
    }
  }
  .adn-content .section-overline { justify-content: flex-start; }
  .adn-content .section-overline::before { display: none; }
  .adn-content .section-title { text-align: left; font-size: clamp(1.8rem, 3.5vw, 3rem); }
  .adn-content .section-subtitle { text-align: left; margin: 0; max-width: 100%; margin-top: 18px; }
  .adn-pillars { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-top: 36px; }
  .adn-pillar {
    padding: 20px 20px;
    border: 1px solid rgba(212,175,55,0.1);
    background: rgba(212,175,55,0.025);
    transition: border-color 0.3s, background 0.3s;
  }
  .adn-pillar:hover { border-color: rgba(212,175,55,0.25); background: rgba(212,175,55,0.05); }
  .pillar-icon { font-size: 1.2rem; margin-bottom: 8px; }
  .pillar-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 1rem; font-weight: 500;
    color: var(--gold-bright); margin-bottom: 5px;
  }
  .pillar-text {
    font-family: 'Barlow', sans-serif;
    font-size: 0.73rem; font-weight: 300;
    color: var(--text-muted); line-height: 1.7;
  }
  .adn-svg-content {
    width: 82%;
    opacity: 0.98;
  }
  .france-map-svg {
    overflow: visible;
  }
  .france-region {
    fill: url(#franceGoldFill);
    stroke: #d4af37;
    stroke-width: 1.15;
    stroke-linejoin: round;
    stroke-linecap: round;
    vector-effect: non-scaling-stroke;
  }
  .france-map-mainland .france-region {
    opacity: 0.73;
  }
  .france-corsica {
    opacity: 0.78;
  }
  .lille-dot {
    fill: #f5e090;
  }
  .lille-ring {
    fill: none;
    stroke: #d4af37;
    stroke-width: 1.4;
    opacity: 0.730;
  }
  .lille-pulse {
    fill: none;
    stroke: #d4af37;
    stroke-width: 1;
    opacity: 0.3;
  }

  .lille-line {
    fill: none;
    stroke: #d4af37;
    stroke-width: 1.2;
    stroke-linecap: round;
    opacity: 0.86;
  }

  .lille-label {
    font-family: "Barlow Condensed", sans-serif;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 3px;
    fill: #d4af37;
  }

  .lille-sub {
    font-family: "Barlow", sans-serif;
    font-size: 7px;
    font-weight: 300;
    letter-spacing: 1.2px;
    fill: rgba(244, 244, 239, 0.55);
  }

  /* ── RÉALISATIONS ── */
  .realisation-card-icon {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: min(78%, 340px);
    max-height: 72%;
    opacity: 0.82;
    user-select: none;
    pointer-events: none;
  }

  .realisation-card-icon img {
    width: 100%;
    height: auto;
    max-height: 320px;
    object-fit: contain;
    display: block;
    filter: drop-shadow(0 18px 34px rgba(0,0,0,0.34));
  }
  .realisation-card:first-child .realisation-card-icon {
    width: min(82%, 420px);
  }

  .realisation-card:first-child .realisation-card-icon img {
    max-height: 420px;
  }

  .realisation-grid-lines {
    position: absolute;
    inset: 0;
    background-image:
      repeating-linear-gradient(
        0deg,
        rgba(212,175,55,0.03) 0px,
        rgba(212,175,55,0.03) 1px,
        transparent 1px,
        transparent 40px
      ),
      repeating-linear-gradient(
        90deg,
        rgba(212,175,55,0.03) 0px,
        rgba(212,175,55,0.03) 1px,
        transparent 1px,
        transparent 40px
      );
  }
  .realisations-section {
    padding: 110px 60px;
    background: linear-gradient(180deg, var(--dark-1) 0%, var(--obsidian) 40%, var(--dark-2) 100%);
  }
  .realisations-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2px;
    max-width: 1280px;
    margin: 0 auto;
  }
  .realisation-card {
    position: relative;
    aspect-ratio: 3/4;
    background: var(--dark-3);
    overflow: hidden;
  }
  .realisation-card:first-child { grid-row: span 2; aspect-ratio: unset; }
  .realisation-card-bg {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: none;
  }

  .realisation-card:hover .realisation-card-bg {
    transform: scale(1.04);
    transition: transform 0.28s cubic-bezier(.25,.46,.45,.94);
  }
  .realisation-overlay {
    position: absolute; inset: 0;
    background: linear-gradient(to top, rgba(7,7,13,0.95) 0%, rgba(7,7,13,0.5) 40%, rgba(7,7,13,0.1) 100%);
    transition: background 0.4s;
  }
  .realisation-card:hover .realisation-overlay {
    background: linear-gradient(to top, rgba(7,7,13,0.98) 0%, rgba(7,7,13,0.7) 50%, rgba(7,7,13,0.3) 100%);
  }
  .realisation-content {
    position: absolute; bottom: 0; left: 0; right: 0;
    padding: 26px 24px;
  }
  .realisation-cat {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 0.56rem; font-weight: 500;
    letter-spacing: 0.45em; text-transform: uppercase;
    color: var(--gold-mid); margin-bottom: 7px;
  }
  .realisation-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 1.4rem; font-weight: 400;
    color: var(--white-fr); line-height: 1.15; margin-bottom: 10px;
  }
  .realisation-card:first-child .realisation-title { font-size: 1.95rem; }
  .realisation-desc {
    font-family: 'Barlow', sans-serif;
    font-size: 0.74rem;
    font-weight: 300;
    color: var(--text-muted);
    line-height: 1.7;
    max-height: 0;
    overflow: hidden;
    opacity: 0;
    transition: none;
  }

  .realisation-card:hover .realisation-desc {
    max-height: 100px;
    opacity: 1;
    transition:
      max-height 0.24s ease,
      opacity 0.2s ease;
  }
  .realisation-arrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 0.57rem;
    font-weight: 500;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--gold);
    margin-top: 11px;
    opacity: 0;
    transform: translateX(-8px);
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    transition: none;
  }

  .realisation-arrow:hover {
    color: var(--gold-bright);
  }

  .realisation-card:hover .realisation-arrow {
    opacity: 1;
    transform: translateX(0);
    transition:
      opacity 0.2s ease,
      transform 0.2s ease,
      color 0.25s ease;
  }
  .r-line { width: 18px; height: 1px; background: var(--gold); }
  .realisation-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 20000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: clamp(16px, 4vw, 48px);
    background: rgba(7, 7, 13, 0.88);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    animation: modalBackdropIn 0.24s ease forwards;
  }

  .realisation-modal-panel {
    position: relative;
    width: min(1040px, 100%);
    max-height: min(86svh, 860px);
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
    background: linear-gradient(145deg, rgba(19,19,31,0.98), rgba(10,10,18,0.98));
    border: 1px solid rgba(212,175,55,0.24);
    box-shadow:
      0 40px 120px rgba(0,0,0,0.68),
      0 0 0 1px rgba(255,255,255,0.03) inset;
    overflow: hidden;
    animation: modalPanelIn 0.28s cubic-bezier(.25,.46,.45,.94) forwards;
  }

  .realisation-modal-visual {
    position: relative;
    min-height: 540px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .realisation-modal-visual::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 50% 45%, rgba(212,175,55,0.08), transparent 48%),
      linear-gradient(to top, rgba(7,7,13,0.66), rgba(7,7,13,0.08));
    pointer-events: none;
  }

  .realisation-modal-grid {
    position: absolute;
    inset: 0;
    background-image:
      repeating-linear-gradient(
        0deg,
        rgba(212,175,55,0.04) 0px,
        rgba(212,175,55,0.04) 1px,
        transparent 1px,
        transparent 44px
      ),
      repeating-linear-gradient(
        90deg,
        rgba(212,175,55,0.04) 0px,
        rgba(212,175,55,0.04) 1px,
        transparent 1px,
        transparent 44px
      );
    opacity: 0.9;
  }

  .realisation-modal-icon {
    position: relative;
    z-index: 2;
    width: min(62%, 420px);
    max-height: 70%;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.92;
  }

  .realisation-modal-icon img {
    width: 100%;
    height: auto;
    max-height: 440px;
    object-fit: contain;
    display: block;
    filter: drop-shadow(0 30px 58px rgba(0,0,0,0.46));
  }

  .realisation-modal-body {
    position: relative;
    z-index: 2;
    padding: clamp(28px, 4vw, 52px);
    display: flex;
    flex-direction: column;
    justify-content: center;
    border-left: 1px solid rgba(212,175,55,0.12);
  }

  .realisation-modal-body h3 {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(2rem, 4vw, 3.3rem);
    font-weight: 300;
    line-height: 1.05;
    color: var(--white-fr);
    margin: 10px 0 18px;
  }

  .realisation-modal-body p {
    font-family: 'Barlow', sans-serif;
    font-size: clamp(0.82rem, 1.6vw, 0.95rem);
    font-weight: 300;
    color: var(--text-body);
    line-height: 1.9;
    max-width: 440px;
  }

  .realisation-modal-cta {
    width: fit-content;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 30px;
    padding: 14px 22px;
    border: 1px solid rgba(212,175,55,0.38);
    background: rgba(212,175,55,0.055);
    color: var(--gold);
    text-decoration: none;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.26em;
    text-transform: uppercase;
    transition:
      background 0.25s ease,
      border-color 0.25s ease,
      color 0.25s ease,
      transform 0.25s ease;
  }

  .realisation-modal-cta:hover {
    background: rgba(212,175,55,0.13);
    border-color: rgba(212,175,55,0.72);
    color: var(--gold-bright);
    transform: translateY(-1px);
  }

  .realisation-modal-close {
    position: fixed;
    top: clamp(14px, 3vw, 28px);
    right: clamp(14px, 3vw, 28px);
    z-index: 20001;
    width: 46px;
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(212,175,55,0.32);
    background: rgba(13,13,22,0.88);
    color: var(--gold);
    font-size: 34px;
    line-height: 1;
    font-family: Arial, sans-serif;
    transition:
      background 0.25s ease,
      color 0.25s ease,
      border-color 0.25s ease,
      transform 0.25s ease;
  }

  .realisation-modal-close:hover {
    background: rgba(212,175,55,0.13);
    border-color: rgba(212,175,55,0.7);
    color: var(--gold-bright);
    transform: rotate(90deg);
  }

  @keyframes modalBackdropIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes modalPanelIn {
    from {
      opacity: 0;
      transform: translateY(18px) scale(0.96);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  /* ── TECH ── */
  .tech-section {
    padding: 110px 60px;
    background: linear-gradient(180deg, var(--dark-2) 0%, var(--dark-1) 100%);
    position: relative;
  }
  .tech-bg-text {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(6rem, 20vw, 18rem);
    font-weight: 700;
    color: rgba(212,175,55,0.025);
    white-space: nowrap; pointer-events: none;
    letter-spacing: -0.04em; user-select: none;
  }
  .tech-inner { max-width: 1200px; margin: 0 auto; position: relative; z-index: 1; }
  .tech-features {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 2px; margin-top: 66px;
  }
  .tech-feature {
    background: var(--dark-3);
    padding: 34px 26px;
    border-top: 2px solid transparent;
    transition: border-color 0.3s, background 0.3s;
    position: relative; overflow: hidden;
  }
  .tech-feature::before {
    content: '';
    position: absolute; bottom: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(212,175,55,0.2), transparent);
  }
  .tech-feature:hover { border-color: var(--gold); background: var(--dark-4); }
  .tech-feature-num {
    font-family: 'Cormorant Garamond', serif;
    font-size: 2.4rem; font-weight: 300;
    color: rgba(212,175,55,0.12); line-height: 1; margin-bottom: 16px;
  }
  .tech-feature-icon { font-size: 1.25rem; margin-bottom: 12px; display: block; }
  .tech-feature-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 1.12rem; font-weight: 500;
    color: var(--gold-bright); margin-bottom: 10px;
  }
  .tech-feature-text {
    font-family: 'Barlow', sans-serif;
    font-size: 0.75rem; font-weight: 300;
    color: var(--text-muted); line-height: 1.75;
    white-space: pre-line;
  }
  .tech-spec-bar {
    margin-top: 14px; height: 2px;
    background: var(--dark-5); position: relative; overflow: hidden;
  }
  .tech-spec-fill {
    position: absolute; top: 0; left: 0; bottom: 0;
    background: linear-gradient(90deg, var(--gold-dark), var(--gold));
    width: 0%;
    transition: width 1.2s cubic-bezier(.25,.46,.45,.94) 0.3s;
  }

  /* ── FRANCE ── */
  .france-section { display: flex; min-height: 540px; position: relative; }
  .france-left {
    width: 50%;
    background: linear-gradient(135deg, var(--blue-fr) 0%, #001670 60%, #00104d 100%);
    padding: 88px 68px;
    display: flex; flex-direction: column; justify-content: center;
    position: relative; z-index: 1;
  }
  .france-left::after {
    content: '';
    position: absolute; top: 0; bottom: 0; right: -36px;
    width: 72px;
    background: inherit;
    transform: skewX(-4deg); z-index: 2;
  }
  .france-right {
    width: 50%;
    background: linear-gradient(225deg, var(--red-fr) 0%, #8b1520 60%, #5c0e16 100%);
    padding: 88px 68px 88px 102px;
    display: flex; flex-direction: column; justify-content: center;
  }
  .france-center-emblem {
    position: absolute; left: 50%; top: 50%;
    transform: translate(-50%, -50%);
    z-index: 10; width: 88px; height: 88px;
    background: var(--gold);
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 0 50px rgba(212,175,55,0.5);
  }
  .france-center-emblem img {
    width: 60%;
    height: 60%;
    object-fit: contain;
  }
  .france-overline {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 0.57rem; font-weight: 500;
    letter-spacing: 0.38em; text-transform: uppercase;
    color: rgba(255,255,255,0.5); margin-bottom: 16px;
  }
  .france-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(1.7rem, 3vw, 2.6rem);
    font-weight: 300; color: var(--white-fr);
    line-height: 1.1; margin-bottom: 20px;
  }
  .france-title strong { font-weight: 600; display: block; }
  .france-text {
    font-family: 'Barlow', sans-serif;
    font-size: 0.78rem; font-weight: 300;
    color: rgba(255,255,255,0.65); line-height: 1.85;
    max-width: 320px;
  }
  .france-list {
    list-style: none; margin-top: 22px;
    display: flex; flex-direction: column; gap: 9px;
  }
  .france-list li {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 0.72rem; font-weight: 400;
    letter-spacing: 0.1em; color: rgba(255,255,255,0.7);
    display: flex; align-items: center; gap: 10px;
  }
  .france-list li::before {
    content: ''; width: 14px; height: 1px;
    background: rgba(255,255,255,0.4); flex-shrink: 0;
  }

  /* ── CONTACT ── */
  .contact-section {
    padding: 110px 36px;
    background: linear-gradient(180deg, var(--dark-2) 0%, var(--obsidian) 100%);
    text-align: center;
  }
  .contact-inner { max-width: 660px; margin: 0 auto; }
  .contact-email {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(1rem, 3vw, 1.7rem);
    font-weight: 300; color: var(--gold);
    text-decoration: none; letter-spacing: 0.06em;
    border-bottom: 1px solid rgba(212,175,55,0.3);
    padding-bottom: 4px;
    transition: color 0.3s, border-color 0.3s;
    word-break: break-all;
  }
  .contact-email:hover { color: var(--gold-bright); border-color: var(--gold); }
  .contact-info {
    display: flex; justify-content: center;
    margin-top: 52px; padding-top: 52px;
    border-top: 1px solid rgba(212,175,55,0.1);
    flex-wrap: wrap; gap: 0;
  }
  .contact-info-item {
    text-align: center;
    padding: 0 32px;
    border-right: 1px solid rgba(212,175,55,0.1);
    margin-bottom: 20px;
  }
  .contact-info-item:last-child { border-right: none; }
  .contact-info-label {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 0.57rem; font-weight: 500;
    letter-spacing: 0.38em; text-transform: uppercase;
    color: var(--gold-mid); margin-bottom: 7px;
  }
  .contact-info-value {
    font-family: 'Barlow', sans-serif;
    font-size: 0.78rem; font-weight: 300;
    color: var(--text-body); line-height: 1.6;
  }

  /* ── FOOTER ── */
  footer {
    background: var(--obsidian);
    border-top: 1px solid rgba(212,175,55,0.08);
    padding: 28px 44px;
    display: flex; align-items: center;
    justify-content: space-between;
    gap: 16px; flex-wrap: wrap;
  }
  .footer-left, .footer-right {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 0.57rem; font-weight: 300;
    letter-spacing: 0.18em; text-transform: uppercase;
    color: var(--text-dim);
  }
  .footer-center { display: flex; gap: 3px; }
  .footer-bar { width: 18px; height: 2px; }

  /* ── ANIMATIONS ── */
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes scrollPulse {
    0%, 100% { opacity: 0.3; transform-origin: top; transform: scaleY(0.7); }
    50%       { opacity: 1;   transform-origin: top; transform: scaleY(1); }
  }
  .fade-up {
    opacity: 0; transform: translateY(26px);
    transition: opacity 0.8s ease, transform 0.8s ease;
  }
  .fade-up.visible { opacity: 1; transform: translateY(0); }
  .fade-up-d1 { transition-delay: 0.1s; }
  .fade-up-d2 { transition-delay: 0.22s; }
  .fade-up-d3 { transition-delay: 0.34s; }
  .fade-up-d4 { transition-delay: 0.46s; }
  .fade-up-d5 { transition-delay: 0.58s; }
  .fade-up-d6 { transition-delay: 0.7s; }

  /* ══════════════════════════════════════════════════════
     RESPONSIVE
  ══════════════════════════════════════════════════════ */

  /* Tablet 768–1024 */
  @media (max-width: 1024px) {
    nav { padding: 22px 32px; }
    nav.scrolled { padding: 14px 32px; }
    .adn-section, .realisations-section, .tech-section, .contact-section { padding-left: 32px; padding-right: 32px; }
    .tech-features { grid-template-columns: repeat(2, 1fr); }
    .realisations-grid { grid-template-columns: repeat(2, 1fr); }
    .realisation-card:first-child { grid-row: span 1; aspect-ratio: 3/4; }
    .realisation-modal-panel {
      grid-template-columns: 1fr;
      width: min(760px, 100%);
      max-height: 88svh;
      overflow-y: auto;
    }
    .realisation-modal-visual {
      min-height: 430px;
    }
    .realisation-modal-body {
      border-left: none;
      border-top: 1px solid rgba(212,175,55,0.12);
    }
  }

  /* Mobile ≤768 */
  @media (max-width: 768px) {
    /* nav */
    nav { padding: 16px 20px; }
    nav.scrolled { padding: 12px 20px; }
    .nav-links { display: none; }
    .nav-hamburger { display: flex; }
    .nav-drawer { display: flex; }

    /* hero */
    .hero-title { font-size: clamp(2.6rem, 11vw, 3.6rem); }
    .hero-tagline { display: none; }
    .hero-divider { height: 28px; margin: 22px auto; }
    .hero-tricolor { margin-bottom: 24px; }
    .hero-content { padding: 0 20px; }
    .hero-actions { gap: 10px; }
    .btn-primary, .btn-secondary { padding: 13px 24px; font-size: 0.64rem; }

    /* band */
    .identity-band-inner { flex-wrap: wrap; }
    .identity-stat {
      width: 50%; flex: none;
      border-right: none;
      border-bottom: 1px solid rgba(212,175,55,0.1);
      padding: 16px 14px;
    }
    .identity-stat:nth-child(odd)  { border-right: 1px solid rgba(212,175,55,0.1); }
    .identity-stat:last-child      { width: 100%; border-bottom: none; border-right: none; }
    .stat-number { font-size: 1.4rem; }

    /* ADN */
    .adn-section { padding: 66px 20px; }
    .adn-grid { grid-template-columns: 1fr; gap: 44px; }
    .adn-visual { max-width: 290px; margin: 0 auto; }
    .adn-frame { aspect-ratio: 1/1; }
    .adn-content .section-title { font-size: clamp(1.7rem, 6vw, 2.4rem); }
    .adn-pillars { grid-template-columns: 1fr; gap: 12px; }

    /* réalisations */
    .realisations-section {
      padding: 66px 20px;
    }
    .realisations-grid {
      grid-template-columns: 1fr;
      gap: 14px;
    }
    .realisation-card,
    .realisation-card:first-child {
      aspect-ratio: unset;
      min-height: 430px;
      grid-row: span 1;
      border: 1px solid rgba(212,175,55,0.08);
      background: var(--dark-3);
    }
    .realisation-card-bg {
      position: absolute;
      inset: 0;
      align-items: flex-start;
      justify-content: center;
      padding-top: 28px;
      transform: none !important;
    }
    .realisation-card:hover .realisation-card-bg {
      transform: none !important;
    }
    .realisation-card-icon {
      width: min(82%, 330px);
      max-height: 210px;
      opacity: 0.48;
      z-index: 1;
    }
    .realisation-card-icon img {
      width: 100%;
      max-height: 210px;
      object-fit: contain;
      filter:
        brightness(0.42)
        contrast(1.08)
        saturate(0.85)
        drop-shadow(0 18px 34px rgba(0,0,0,0.45));
    }
    .realisation-grid-lines {
      opacity: 0.45;
      z-index: 1;
    }
    .realisation-overlay {
      z-index: 2;
      background:
        linear-gradient(
          to bottom,
          rgba(7,7,13,0.35) 0%,
          rgba(7,7,13,0.65) 34%,
          rgba(7,7,13,0.94) 58%,
          rgba(7,7,13,1) 100%
        );
    }
    .realisation-content {
      z-index: 3;
      bottom: 0;
      padding: 24px 22px 26px;
      background:
        linear-gradient(
          to top,
          rgba(7,7,13,0.98) 0%,
          rgba(7,7,13,0.94) 70%,
          rgba(7,7,13,0) 100%
        );
    }
    .realisation-cat {
      font-size: 0.58rem;
      letter-spacing: 0.42em;
      margin-bottom: 12px;
    }
    .realisation-title,
    .realisation-card:first-child .realisation-title {
      font-size: 1.55rem !important;
      line-height: 1.08;
      margin-bottom: 12px;
      color: var(--white-fr);
    }
    .realisation-desc {
      max-height: none;
      opacity: 1;
      color: rgba(244,244,239,0.68);
      font-size: 0.82rem;
      line-height: 1.75;
      overflow: visible;
    }
    .realisation-arrow {
      opacity: 1;
      transform: none;
      margin-top: 18px;
    }
    .realisation-card-bg {
      transition: none;
    }
  .realisation-overlay {
    background: linear-gradient(
      to top,
      rgba(7,7,13,1)    0%,
      rgba(7,7,13,0.97) 30%,
      rgba(7,7,13,0.75) 55%,
      rgba(7,7,13,0.15) 82%,
      rgba(7,7,13,0)   100%
    );
  }
    .realisation-card:hover .realisation-card-bg {
        transform: none;
    }
    .realisation-title    { font-size: 1.25rem !important; }
    .realisation-content  { padding: 20px 18px; }

    /* tech */
    .tech-section { padding: 66px 20px; }
    .tech-features { grid-template-columns: 1fr; gap: 3px; margin-top: 44px; }
    .tech-feature { padding: 28px 22px; }
    .tech-bg-text { font-size: 4.5rem; }

    /* france */
    .france-section { flex-direction: column; min-height: unset; }
    .france-left, .france-right { width: 100%; padding: 60px 24px; }
    .france-left::after { display: none; }
    .france-right { padding-left: 24px; }
    .france-center-emblem { display: none; }
    .france-text { max-width: 100%; }

    /* contact */
    .contact-section { padding: 66px 20px; }
    .contact-info { flex-direction: column; align-items: center; gap: 24px; padding-top: 36px; margin-top: 36px; }
    .contact-info-item { border-right: none; padding: 0; }

    /* footer */
    footer {
      padding: 24px 20px;
      flex-direction: column;
      align-items: center;
      text-align: center; gap: 12px;
    }

    /* global */
    .section-header { margin-bottom: 44px; }
    .section-title  { font-size: clamp(1.7rem, 7vw, 2.6rem); }
    .realisation-card-icon {
      width: min(62%, 240px);
      max-height: 72%;
    }
    .realisation-card-icon img {
      max-height: 210px;
    }
    .realisation-modal-backdrop {
      align-items: flex-end;
      padding: 12px;
    }

    .realisation-modal-panel {
      width: 100%;
      max-height: 88svh;
      overflow-y: auto;
    }

    .realisation-modal-visual {
      min-height: 300px;
    }

    .realisation-modal-icon {
      width: min(68%, 280px);
    }

    .realisation-modal-icon img {
      max-height: 280px;
    }

    .realisation-modal-body {
      padding: 26px 22px 28px;
    }

    .realisation-modal-body h3 {
      font-size: clamp(1.75rem, 9vw, 2.45rem);
    }

    .realisation-modal-cta {
      width: 100%;
      text-align: center;
    }

    .realisation-modal-close {
      width: 42px;
      height: 42px;
      font-size: 30px;
    }
  }

  /* Very small phones */
  @media (max-width: 370px) {
    .hero-title { font-size: 2.5rem; }
    .hero-title-brand { font-size: 0.62rem; letter-spacing: 0.28em; }
    .hero-actions { flex-direction: column; align-items: stretch; }
    .btn-primary, .btn-secondary { text-align: center; width: 100%; }
    .realisation-modal-backdrop {
      padding: 8px;
    }

    .realisation-modal-visual {
      min-height: 240px;
    }

    .realisation-modal-body {
      padding: 22px 18px 24px;
    }

    .realisation-modal-cta {
      font-size: 0.62rem;
      letter-spacing: 0.2em;
      padding: 13px 16px;
    }
  }
`;

/* ─────────────────────────────────────────────────────────────
   SVG COMPONENTS
───────────────────────────────────────────────────────────── */
const MadeInFranceIcon = () => (
  <img
    src="/assets/images/fab_France.png"
    alt="Fabriqué en France"
    className="made-france-logo"
  />
);

const NavEmblem = () => (
  <img
    src="/assets/icons/GALLUS_sans_dons_heade.svg"
    width="36"
    height="36"
  ></img>
);

const MapFrance = () => {
  const mainlandRegions = franceMap.locations.filter(
    (region) => region.id !== "corse",
  );

  const corsica = franceMap.locations.find((region) => region.id === "corse");

  return (
    <svg
      className="adn-svg-content france-map-svg"
      viewBox={franceMap.viewBox}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Carte de France avec localisation à Lille"
    >
      <defs>
        <linearGradient id="franceGoldFill" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#d4af37" stopOpacity="0.22" />
          <stop offset="55%" stopColor="#b8962e" stopOpacity="0.14" />
          <stop offset="100%" stopColor="#7a5f14" stopOpacity="0.08" />
        </linearGradient>

        <filter
          id="franceGoldGlow"
          x="-30%"
          y="-30%"
          width="160%"
          height="160%"
        >
          <feGaussianBlur stdDeviation="2.2" result="blur" />
          <feColorMatrix
            in="blur"
            type="matrix"
            values="
              1 0 0 0 0.83
              0 1 0 0 0.69
              0 0 1 0 0.22
              0 0 0 0.55 0
            "
            result="goldBlur"
          />
          <feMerge>
            <feMergeNode in="goldBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <g className="france-map-mainland" filter="url(#franceGoldGlow)">
        {mainlandRegions.map((region) => (
          <path key={region.id} d={region.path} className="france-region" />
        ))}
      </g>

      {corsica && (
        <path d={corsica.path} className="france-region france-corsica" />
      )}

      {/* Lille marker.
          Coordinates are in the SVG map coordinate system.
          Adjust slightly if needed depending on the package viewBox. */}
      <g className="lille-marker" transform="translate(342 34)">
        <circle className="lille-pulse" r="17" />
        <circle className="lille-ring" r="9" />
        <circle className="lille-dot" r="4.2" />

        <path className="lille-line" d="M8 -5 C34 -22 58 -26 84 -26" />

        <text className="lille-label" x="90" y="-23">
          LILLE
        </text>

        <text className="lille-sub" x="90" y="-8">
          HAUTS-DE-FRANCE
        </text>
      </g>
    </svg>
  );
};

/* ─────────────────────────────────────────────────────────────
   DATA
───────────────────────────────────────────────────────────── */
const realisations = [
  {
    cat: "Extensions Tactiques",
    title: "Kit de conversions",
    desc: "Équipements de stabilisation additionnels pour de nombreux types d'armements individuels.",
    bg: "linear-gradient(135deg, #0d1520 0%, #0a1830 50%, #0d1520 100%)",
    icon: <img src="/assets/images/conversion.png" alt="conversion" />,
  },
  {
    cat: "Équipements Tactiques",
    title: "Holsters & Étuis de Combat",
    desc: "Étuis polymères haute précision, compatibles rail Picatinny, pour unités d'élite. Résistance aux chocs et aux UV garantie.",
    bg: "linear-gradient(135deg, #12100a 0%, #1a1508 50%, #0e0d08 100%)",
    icon: <img src="/assets/images/holster.png" alt="holster" />,
  },
  {
    cat: "Systèmes MOLLE",
    title: "Accessoires de Portage",
    desc: "Pièces de connexion, adaptateurs et supports compatibles standard OTAN.",
    bg: "linear-gradient(135deg, #0a1215 0%, #0d1a1e 50%, #070e10 100%)",
    icon: <img src="/assets/images/molle.png" alt="molle" />,
  },
  {
    cat: "Système adaptés",
    title: "Équipements Spécialisés",
    desc: "Pièces sur mesure pour unités d'intervention spécialisées, tolérances ±0.1mm.",
    bg: "linear-gradient(135deg, #150a10 0%, #1e0d17 50%, #0f0710 100%)",
    icon: <img src="/assets/images/specialise.png" alt="specialise" />,
  },
  {
    cat: "Optique & Visée",
    title: "Supports & Adaptateurs",
    desc: "Montures légères et rigides pour systèmes d'optique militaires et civils.",
    bg: "linear-gradient(135deg, #0a1510 0%, #0d1e15 50%, #07100a 100%)",
    icon: <img src="/assets/images/support.png" alt="support" />,
  },
];

const techFeatures = [
  {
    num: "01",
    icon: <img src="/assets/icons/leger.svg" alt="leger" />,
    title: "Légèreté Extrême",
    text: "Densité de 1,01 g/cm³ — jusqu'à 50% plus léger que l'aluminium pour des performances équivalentes.",
    spec: 90,
  },
  {
    num: "02",
    icon: <img src="/assets/icons/feu.svg" alt="resistant" />,
    title: "Résistance Thermique",
    text: "Tenue continue jusqu'à 170 °C. Stabilité dimensionnelle garantie dans les environnements opérationnels sévères.",
    spec: 85,
  },
  {
    num: "03",
    icon: <img src="/assets/icons/bouclier.svg" alt="bouclier" />,
    title: "Tenue Chimique",
    text: "Résistance aux hydrocarbures, lubrifiants et solvants. Compatible avec les milieux agressifs du terrain.",
    spec: 95,
  },
  {
    num: "04",
    icon: <img src="/assets/icons/cible.svg" alt="precis" />,
    title: "Précision ±0.1mm",
    text: "Lit de poudre MJF haute résolution. Géométries complexes réalisables sans outillage ni moule.\n\n",
    spec: 98,
  },
];

const navItems = [
  { label: "Notre ADN", href: "#adn" },
  { label: "Réalisations", href: "#realisations" },
  { label: "Technologie", href: "#tech" },
  { label: "Made in France", href: "#france" },
];

/* ─────────────────────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────────────────────── */
export default function GallusIndustrie() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hasMouse, setHasMouse] = useState(false);
  const [selectedRealisation, setSelectedRealisation] = useState(null);

  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const mousePos = useRef({ x: -100, y: -100 });
  const ringPos = useRef({ x: -100, y: -100 });
  const rafId = useRef(null);

  /* detect real pointer once */
  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(pointer: fine)").matches
    ) {
      setHasMouse(true);
    }
  }, []);

  /* scroll */
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  /* body scroll lock for menu + realisation modal */
  useEffect(() => {
    document.body.style.overflow =
      menuOpen || selectedRealisation ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen, selectedRealisation]);
  useEffect(() => {
    if (!selectedRealisation) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setSelectedRealisation(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [selectedRealisation]);
  useEffect(() => {
    if (!selectedRealisation) {
      ringRef.current?.classList.remove("hovering");
    }
  }, [selectedRealisation]);

  /* cursor animation loop — only on real pointer devices */
  useEffect(() => {
    if (!hasMouse) return;

    const lerp = (a, b, t) => a + (b - a) * t;

    const onMove = (e) => {
      mousePos.current = { x: e.clientX, y: e.clientY };
    };

    const tick = () => {
      const { x, y } = mousePos.current;

      // dot follows mouse instantly
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
      }

      ringPos.current.x = lerp(ringPos.current.x, x, 0.09);
      ringPos.current.y = lerp(ringPos.current.y, y, 0.09);

      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${ringPos.current.x}px, ${ringPos.current.y}px, 0) translate(-50%, -50%)`;
      }

      rafId.current = requestAnimationFrame(tick);
    };

    const onOver = (e) => {
      if (e.target.closest("a,button"))
        ringRef.current?.classList.add("hovering");
    };
    const onOut = (e) => {
      if (e.target.closest("a,button"))
        ringRef.current?.classList.remove("hovering");
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseout", onOut);
    rafId.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [hasMouse]);

  /* fade-up on scroll */
  useEffect(() => {
    const els = document.querySelectorAll(".fade-up");
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        }),
      { threshold: 0.1 },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  /* spec bar fill on scroll */
  useEffect(() => {
    const bars = document.querySelectorAll(".tech-spec-fill");
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting)
            e.target.style.width = e.target.dataset.width + "%";
        }),
      { threshold: 0.3 },
    );
    bars.forEach((b) => {
      b.style.width = "0%";
      obs.observe(b);
    });
    return () => obs.disconnect();
  }, []);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  return (
    <>
      <style>{STYLES}</style>

      {/* ── CURSOR (only rendered when hasMouse) ── */}
      {hasMouse && (
        <>
          <div className="cursor-dot" ref={dotRef} />
          <div className="cursor-ring" ref={ringRef} />
        </>
      )}

      {/* ── MOBILE DRAWER ── */}
      <div
        className={`nav-drawer ${menuOpen ? "open" : ""}`}
        aria-hidden={!menuOpen}
      >
        {navItems.map((item, i) => (
          <a
            key={i}
            href={item.href}
            className="nav-drawer-link"
            onClick={closeMenu}
          >
            {item.label}
          </a>
        ))}
        <a href="#contact" className="nav-drawer-cta" onClick={closeMenu}>
          Contact
        </a>
        <div className="nav-drawer-tricolor">
          <span style={{ background: "#002395" }} />
          <span style={{ background: "#f4f4ef" }} />
          <span style={{ background: "#ed2939" }} />
        </div>
      </div>

      {/* ── NAV ── */}
      <nav className={scrolled ? "scrolled" : ""}>
        <a href="#hero" className="nav-logo">
          <NavEmblem />
          <div className="nav-logo-text">
            Gallus
            <span className="nav-logo-sub">Industrie · France</span>
          </div>
        </a>
        <ul className="nav-links">
          {navItems.map((item, i) => (
            <li key={i}>
              <a href={item.href}>{item.label}</a>
            </li>
          ))}
          <li>
            <a href="#contact" className="nav-cta">
              Contact
            </a>
          </li>
        </ul>
        <button
          className={`nav-hamburger ${menuOpen ? "open" : ""}`}
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
        >
          <span />
          <span />
          <span />
        </button>
      </nav>

      {/* ── HERO ── */}
      <section className="hero" id="hero">
        <div className="hero-noise" />
        <div className="hero-lines" />
        <div className="hero-content">
          <div className="hero-tricolor">
            <div className="tricolor-bar" style={{ background: "#002395" }} />
            <div className="tricolor-bar" style={{ background: "#f4f4ef" }} />
            <div className="tricolor-bar" style={{ background: "#ed2939" }} />
          </div>
          <div className="hero-overline">
            Équipements Militaires · Impression 3D MJF · France
          </div>
          <h1 className="hero-title">
            L'excellence
            <br />
            <em>française</em>
            <br />
            au service
            <br />
            du terrain
          </h1>
          <span className="hero-title-brand">Gallus Industrie</span>
          <div className="hero-divider" />
          <p className="hero-tagline">
            Concepteur et fabricant français d'équipements militaires et
            tactiques en polyamide PA12. Précision suisse, âme gauloise.
          </p>
          <div className="hero-actions">
            <a href="#realisations" className="btn-primary">
              Découvrir nos réalisations
            </a>
            <a href="#contact" className="btn-secondary">
              Nous contacter
            </a>
          </div>
        </div>
        <div className="hero-scroll">
          <span>Défiler</span>
          <div className="scroll-line" />
        </div>
      </section>

      {/* ── IDENTITY BAND ── */}
      <div className="identity-band">
        <div className="identity-band-inner">
          {[
            { num: "100%", label: "Fabrication\nFrançaise" },
            { num: "PA12", label: "Polyamide\nHaute Performance" },
            { num: "MJF", label: "Fusion\nMulti Jet" },
            { num: "±0.1", label: "Précision\nen millimètre" },
            { num: "NF", label: "Normes\nEuropéennes" },
          ].map((s, i) => (
            <div className="identity-stat" key={i}>
              <div className="stat-number">{s.num}</div>
              <div className="stat-label" style={{ whiteSpace: "pre-line" }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── ADN ── */}
      <section className="adn-section" id="adn">
        <div className="adn-grid">
          <div className="adn-visual fade-up">
            <div className="adn-frame">
              <div className="adn-frame-corner tl" />
              <div className="adn-frame-corner tr" />
              <div className="adn-frame-corner bl" />
              <div className="adn-frame-corner br" />
              <MapFrance />
            </div>
            <div className="adn-made-france-box">
              <MadeInFranceIcon />
            </div>
          </div>
          <div className="adn-content">
            <div className="section-overline fade-up">Notre ADN</div>
            <h2 className="section-title fade-up fade-up-d1">
              Forgé en <em>France</em>,<br />
              conçu pour
              <br />
              l'excellence
            </h2>
            <p className="section-subtitle fade-up fade-up-d2">
              Gallus Industrie naît d'une conviction : l'industrie de défense
              française mérite un partenaire local, réactif et souverain. Nous
              concevons et fabriquons chaque pièce sur le sol français, avec des
              matériaux sélectionnés pour leur fiabilité en conditions
              opérationnelles.
            </p>
            <div className="adn-pillars">
              {[
                {
                  icon: (
                    <img
                      src="/assets/icons/usine.svg"
                      alt="Fabriqué en France"
                    />
                  ),
                  title: "Souveraineté",
                  text: "Conception, fabrication et contrôle qualité intégralement réalisés en France.",
                },
                {
                  icon: (
                    <img src="/assets/icons/sur_mesure.svg" alt="Sur mesure" />
                  ),
                  title: "Sur mesure",
                  text: "Du prototype à la série, chaque projet s'adapte à vos spécifications exactes.",
                },
                {
                  icon: (
                    <img
                      src="/assets/icons/confidentiel.svg"
                      alt="Confidentiel"
                    />
                  ),
                  title: "Confidentialité",
                  text: "Processus sécurisés pour les applications sensibles et les marchés défense.",
                },
                {
                  icon: <img src="/assets/icons/rapide.svg" alt="Reactif" />,
                  title: "Réactivité",
                  text: "Délais raccourcis grâce à la fabrication additive, sans moule ni outillage.",
                },
              ].map((p, i) => (
                <div className={`adn-pillar fade-up fade-up-d${i + 3}`} key={i}>
                  <div className="pillar-icon">{p.icon}</div>
                  <div className="pillar-title">{p.title}</div>
                  <div className="pillar-text">{p.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── RÉALISATIONS ── */}
      <section className="realisations-section" id="realisations">
        <div className="section-header">
          <div className="section-overline fade-up">Nos Réalisations</div>
          <h2 className="section-title fade-up fade-up-d1">
            Des équipements <em>pensés</em>
            <br />
            pour le terrain
          </h2>
          <p className="section-subtitle fade-up fade-up-d2">
            De l'étude de faisabilité à la livraison, nous accompagnons les
            forces armées, les unités d'élite et les industriels de la défense.
          </p>
        </div>
        <div className="realisations-grid">
          {realisations.map((r, i) => (
            <article className="realisation-card" key={i}>
              <div className="realisation-card-bg" style={{ background: r.bg }}>
                <div className="realisation-card-icon">{r.icon}</div>

                <div className="realisation-grid-lines" aria-hidden="true" />
              </div>

              <div className="realisation-overlay" />

              <div className="realisation-content">
                <div className="realisation-cat">{r.cat}</div>
                <div className="realisation-title">{r.title}</div>
                <div className="realisation-desc">{r.desc}</div>

                <button
                  type="button"
                  className="realisation-arrow"
                  onClick={() => setSelectedRealisation(r)}
                  aria-label={`En savoir plus sur ${r.title}`}
                >
                  <span className="r-line" />
                  En savoir plus
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── TECH PA12 ── */}
      <section className="tech-section" id="tech">
        <div className="tech-bg-text">PA12</div>
        <div className="tech-inner">
          <div className="section-header">
            <div className="section-overline fade-up">Technologie</div>
            <h2 className="section-title fade-up fade-up-d1">
              Le <em>PA12</em> comme
              <br />
              matériau de souveraineté
            </h2>
            <p className="section-subtitle fade-up fade-up-d2">
              Le polyamide 12 sur lit de poudre (MJF) offre un rapport
              résistance/masse inégalé, une précision dimensionnelle
              sub-millimétrique et une liberté géométrique totale — sans
              compromis opérationnel.
            </p>
          </div>
          <div className="tech-features">
            {techFeatures.map((f, i) => (
              <div className={`tech-feature fade-up fade-up-d${i + 1}`} key={i}>
                <div className="tech-feature-num">{f.num}</div>
                <span className="tech-feature-icon">{f.icon}</span>
                <div className="tech-feature-title">{f.title}</div>
                <div className="tech-feature-text">{f.text}</div>
                <div className="tech-spec-bar">
                  <div className="tech-spec-fill" data-width={f.spec} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MADE IN FRANCE ── */}
      <section className="france-section" id="france">
        <div className="france-left">
          <div className="france-overline">Ancrage Territorial</div>
          <h2 className="france-title">
            Fièrement
            <br />
            <strong>Made in France</strong>
          </h2>
          <p className="france-text">
            Nos ateliers sont implantés au cœur de la France. Chaque commande
            contribue à l'économie locale et renforce la filière industrielle
            nationale de défense.
          </p>
          <ul className="france-list">
            <li>Conception 100% française</li>
            <li>Matières premières sourcées en Europe</li>
            <li>Contrôle qualité intégré sur site</li>
            <li>Filière industrielle souveraine</li>
          </ul>
        </div>
        <div className="france-center-emblem">
          <img src="/assets/icons/lien.svg" alt="lien france" />
        </div>
        <div className="france-right">
          <div className="france-overline">Engagement Défense</div>
          <h2 className="france-title">
            Au service
            <br />
            <strong>des Forces</strong>
          </h2>
          <p className="france-text">
            Nous collaborons avec les forces armées françaises, la gendarmerie
            et les industriels du secteur défense. Notre réactivité et
            discrétion font de nous un partenaire de confiance pour les projets
            les plus sensibles.
          </p>
          <ul className="france-list">
            <li>Homologation sur demande</li>
            <li>Prototypage rapide sous 72h</li>
            <li>NDA & confidentialité garantis</li>
            <li>Livraison sécurisée nationale</li>
          </ul>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section className="contact-section" id="contact">
        <div className="contact-inner">
          <div className="section-overline fade-up">Contact</div>
          <h2 className="section-title fade-up fade-up-d1">
            Parlons de votre
            <br />
            <em>projet</em>
          </h2>
          <p
            className="section-subtitle fade-up fade-up-d2"
            style={{ marginBottom: 40 }}
          >
            Du besoin opérationnel au prototype fonctionnel, notre équipe vous
            accompagne à chaque étape. Contactez-nous pour discuter de vos
            spécifications.
          </p>
          <a
            href="mailto:contact@gallus-industrie.fr"
            className="contact-email fade-up fade-up-d3"
          >
            contact@gallus-industrie.fr
          </a>
          <div className="contact-info fade-up fade-up-d4">
            {[
              { label: "Localisation", value: "France métropolitaine" },
              { label: "Réponse", value: "Sous 24h ouvrées" },
              { label: "Prototypage", value: "Dès 72 heures" },
            ].map((item, i) => (
              <div className="contact-info-item" key={i}>
                <div className="contact-info-label">{item.label}</div>
                <div className="contact-info-value">{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {selectedRealisation && (
        <div
          className="realisation-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={selectedRealisation.title}
          onClick={() => {
            ringRef.current?.classList.remove("hovering");
            setSelectedRealisation(null);
          }}
        >
          <button
            type="button"
            className="realisation-modal-close"
            onClick={(e) => {
              e.stopPropagation();
              ringRef.current?.classList.remove("hovering");
              setSelectedRealisation(null);
            }}
            aria-label="Fermer la réalisation"
          >
            ×
          </button>

          <div
            className="realisation-modal-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="realisation-modal-visual"
              style={{ background: selectedRealisation.bg }}
            >
              <div className="realisation-modal-grid" aria-hidden="true" />

              <div className="realisation-modal-icon">
                {selectedRealisation.icon}
              </div>
            </div>

            <div className="realisation-modal-body">
              <div className="realisation-cat">{selectedRealisation.cat}</div>

              <h3>{selectedRealisation.title}</h3>

              <p>{selectedRealisation.desc}</p>

              <a
                href={`mailto:contact@gallus-industrie.fr?subject=${encodeURIComponent(
                  `Demande d'information - ${selectedRealisation.title}`,
                )}`}
                className="realisation-modal-cta"
                onClick={() => {
                  ringRef.current?.classList.remove("hovering");
                }}
              >
                Parler d’un projet similaire
              </a>
            </div>
          </div>
        </div>
      )}
      {/* ── FOOTER ── */}
      <footer>
        <div className="footer-left">
          © 2026 Gallus Industrie — Tous droits réservés
        </div>
        <div className="footer-center">
          <div className="footer-bar" style={{ background: "#002395" }} />
          <div className="footer-bar" style={{ background: "#f4f4ef" }} />
          <div className="footer-bar" style={{ background: "#ed2939" }} />
        </div>
        <div className="footer-right">Fabrication Française · MJF PA12</div>
      </footer>
    </>
  );
}
